import { Objects } from '../object-definitions'

type SubscriptionFilter = (subscription: ActiveSubscription) => boolean

export type ActiveSubscription = Parse.Object.IncludePointerAttributes<Objects.SubscriptionPeriod.Fetched, ['product', 'productPlan']>

export namespace Subscription {
    function has(
        productGroup: Objects.SubscriptionProduct.Group.Any[],
        activeSubscriptions: ActiveSubscription[],
        feature: keyof Pick<Objects.SubscriptionProduct.Attributes, 'enableMailingListPro' | 'enableFreeListUpdates'> | undefined
    ) {
        if (!activeSubscriptions || activeSubscriptions.length === 0) {
            return false
        }

        const splitPaymentSubscriptionProductMap: { [productID: string]: { count: number } | undefined } = {}

        for (const subscriptionPeriod of activeSubscriptions) {
            const subscriptionProductGroup = subscriptionPeriod.get('productGroup')
            const isSplitPayment = subscriptionPeriod.get('isSplitPayment')
            const subscriptionProduct = subscriptionPeriod.get('product')

            const subscriptionProductHasFeature = feature ? !!subscriptionProduct.get(feature) : true

            const isSinglePaymentMLP = productGroup.includes(subscriptionProductGroup) && !isSplitPayment && subscriptionProductHasFeature

            if (isSinglePaymentMLP) {
                return true
            }

            if (!productGroup.includes(subscriptionProductGroup)) {
                continue
            }

            if (!isSplitPayment) {
                continue
            }

            if (!subscriptionProductHasFeature) {
                continue
            }

            let product = splitPaymentSubscriptionProductMap[subscriptionProduct.id]
            if (!product) {
                product = { count: 0 }
                splitPaymentSubscriptionProductMap[subscriptionProduct.id] = product
            }

            product.count++

            if (product.count >= 2) {
                return true
            }
        }

        return false
    }

    export function hasCreditSubscription(activeSubscriptions: ActiveSubscription[]) {
        return has(['credits'], activeSubscriptions, undefined)
    }

    export function hasTeamOrCreditSubscription(activeSubscriptions: ActiveSubscription[]) {
        return has(['default'], activeSubscriptions, undefined) || hasCreditSubscription(activeSubscriptions)
    }

    export function hasMailingListProBenefit(activeSubscriptions: ActiveSubscription[]) {
        return has(['mailing-list-pro'], activeSubscriptions, undefined) || has(['credits'], activeSubscriptions, 'enableMailingListPro')
    }

    export function hasFacebookCustomAudiencesBenefit(activeSubscriptions: ActiveSubscription[]) {
        return has(['default'], activeSubscriptions, undefined) || has(['credits'], activeSubscriptions, undefined)
    }

    export function hasDownloadBadAddressesBenefit(activeSubscriptions: ActiveSubscription[]) {
        return has(['default'], activeSubscriptions, undefined) || has(['credits'], activeSubscriptions, undefined)
    }

    export function hasFreeListUpdates(activeSubscriptions: ActiveSubscription[]) {
        return has(['credits'], activeSubscriptions, 'enableFreeListUpdates')
    }

    export function getFirstFullDefaultSubscriptionPeriod(subscriptions: ActiveSubscription[]): ActiveSubscription | undefined {
        return getFirstFullSubscriptionPeriod(subscriptions, [(subscription) => subscription.get('productGroup') === 'default'])
    }

    export function filterFullSubscriptions(subscriptions: ActiveSubscription[]) {
        const splitSubscriptionsProducts: { [key: string]: Objects.SubscriptionPeriod.Parse } = {}
        const fullSubscription: ActiveSubscription[] = []

        for (const subscription of subscriptions) {
            // if (!productGroups.includes(subscription.get('productGroup'))) {
            //     continue
            // }

            if (!subscription.get('isSplitPayment')) {
                fullSubscription.push(subscription)
                continue
            }

            if (!splitSubscriptionsProducts[subscription.get('product').id]) {
                splitSubscriptionsProducts[subscription.get('product').id] = subscription
            } else {
                // Get Latest
                fullSubscription.push(subscription)
                break
            }
        }

        return fullSubscription
    }

    export function getFirstFullSubscriptionPeriod(subscriptionPeriods: ActiveSubscription[], filters: SubscriptionFilter[] | undefined) {
        if (!subscriptionPeriods || subscriptionPeriods.length === 0) {
            return
        }

        const foundProductPlans: { [key: string]: ActiveSubscription } = {}

        for (const subscriptionPeriod of subscriptionPeriods) {
            if (filters && filters.length > 0) {
                const passedAllFilters = filters.every((filter) => {
                    return filter(subscriptionPeriod)
                })

                if (!passedAllFilters) {
                    continue
                }
            }

            if (!subscriptionPeriod.get('isSplitPayment')) {
                return subscriptionPeriod
            }

            if (!foundProductPlans[subscriptionPeriod.get('productPlan').id]) {
                foundProductPlans[subscriptionPeriod.get('productPlan').id] = subscriptionPeriod
            } else {
                return foundProductPlans[subscriptionPeriod.get('productPlan').id]
            }
        }

        return undefined
    }

    export function withChildAccount(subscription: ActiveSubscription) {
        return subscription.get('product').get('childAccountCount') > 0
    }
}
